/**
 * data/customers.ts
 * Shared customer data across M11, M15, M16 (and beyond).
 * In production: replace with API calls / AsyncStorage.
 */

import type { CustomerLabel } from '../screens/M15CustomerListScreen';
import type { EditField } from '../components/EditModal';
import { getSavedCustomersLocal } from './savedCustomers';

export interface CustomerNote {
  id: string;
  type: 'journal' | 'gift' | 'claims';
  content: string;
  date: string;
  author: string;
}

export interface CustomerDependent {
  id: string;
  name: string;
  relationship: 'spouse' | 'child' | 'parent' | 'other';
  dob: string;
  age: number;
  gender: 'Nam' | 'Nữ';
  occupation?: string;
}

export interface CustomerIllustration {
  id: string;
  proposalName: string;
  premium: string;          // "15.209" (ngàn đồng)
  sumAssured: string;       // "500.000.000"
  term: string;             // "20 năm"
  freq: string;             // "Hàng năm"
  selectedRiders: string[];
  topUpAmount: number;
  topUpStartYear: number;
  topUpDuration: number;
  r5Mode: 'complimentary' | 'attached';
  lastUpdated: string;
  expiryDate: string;
  stage: 'new' | 'negotiating' | 'closed';
  createdAt?: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  // Extended for restore to M07
  customerName: string;
  customerAge: number;
  customerGender: 'Nam' | 'Nữ';
}

export interface CustomerFull {
  id: string;                          // MÃ KH (unique, dùng để navigate)
  name: string;
  occupation: string;
  category: 'main' | 'dependent';
  dependentsCount: number;
  dob: string;                         // "24/01/1992"
  age: number;
  gender: 'Nam' | 'Nữ';
  phone: string;
  email: string;
  labels: CustomerLabel[];
  address?: string;
  idNumber?: string;                   // CCCD
  createdAt: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  // Relations
  illustrations: CustomerIllustration[];
  notes: CustomerNote[];
  dependents: CustomerDependent[];
}

/** 9 trường có thể chỉnh sửa qua EditModal (dùng chung cho M15 + M16) */
export interface CustomerEditableFields {
  name: string;
  occupation: string;
  dob: string;
  gender: 'Nam' | 'Nữ';
  phone: string;
  email: string;
  category: 'main' | 'dependent';
  dependentsCount: number;
  labels: CustomerLabel[];
}

/**
 * Build EditField[] cho EditModal chỉnh sửa thông tin khách hàng (9 fields).
 * Pattern tham khảo: data/editModals.ts (buildXumMainFields, buildR1Fields, ...).
 * Dùng chung cho M15 (Customer) và M16 (CustomerFull) — cả 2 đều có 9 field trên.
 * Generic `C extends CustomerEditableFields` để giữ nguyên type gốc (Customer /
 * CustomerFull) khi spread, tránh mất các field phụ (id, age, createdAt, ...).
 */
export function buildCustomerEditFields<C extends CustomerEditableFields>(
  customer: C,
  setCustomer: (c: C) => void,
): EditField[] {
  return [
    {
      index: 1,
      label: 'Họ và tên',
      type: 'text',
      value: customer.name,
      onChange: (v) => setCustomer({ ...customer, name: v }),
    },
    {
      index: 2,
      label: 'Nghề nghiệp',
      type: 'text',
      value: customer.occupation,
      onChange: (v) => setCustomer({ ...customer, occupation: v }),
    },
    {
      index: 3,
      label: 'Ngày sinh',
      type: 'text',
      value: customer.dob,
      hint: 'Định dạng: dd/MM/yyyy',
      onChange: (v) => setCustomer({ ...customer, dob: v }),
    },
    {
      index: 4,
      label: 'Giới tính',
      type: 'dropdown',
      value: customer.gender,
      options: [
        { label: 'Nam', value: 'Nam' },
        { label: 'Nữ', value: 'Nữ' },
      ],
      onChange: (v) => setCustomer({ ...customer, gender: v as 'Nam' | 'Nữ' }),
    },
    {
      index: 5,
      label: 'Số điện thoại',
      type: 'text',
      value: customer.phone,
      onChange: (v) => setCustomer({ ...customer, phone: v }),
    },
    {
      index: 6,
      label: 'Email',
      type: 'text',
      value: customer.email,
      onChange: (v) => setCustomer({ ...customer, email: v }),
    },
    {
      index: 7,
      label: 'Phân loại',
      type: 'dropdown',
      value: customer.category,
      options: [
        { label: 'Bên mua bảo hiểm', value: 'main' },
        { label: 'Người phụ thuộc', value: 'dependent' },
      ],
      onChange: (v) =>
        setCustomer({ ...customer, category: v as 'main' | 'dependent' }),
    },
    {
      index: 8,
      label: 'Số người phụ thuộc',
      type: 'stepper',
      value: String(customer.dependentsCount),
      min: 0,
      max: 10,
      step: 1,
      onChange: (v) =>
        setCustomer({ ...customer, dependentsCount: parseInt(v, 10) || 0 }),
    },
    {
      index: 9,
      label: 'Ghi chú (nhãn)',
      type: 'text',
      value: customer.labels.join(', '),
      hint: 'Các nhãn phân cách bằng dấu phẩy. Ví dụ: vip, potential',
      onChange: (v) => {
        const labels = v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean) as CustomerLabel[];
        setCustomer({ ...customer, labels });
      },
    },
  ];
}

export function findCustomerById(idOrName: string): CustomerFull | undefined {
  if (!idOrName) return undefined;
  const target = idOrName.trim().toLowerCase();

  const localCust = getSavedCustomersLocal().find(
    (c) => c.id.toLowerCase() === target || c.name.toLowerCase() === target
  );
  if (localCust) {
    return {
      id: localCust.id,
      name: localCust.name,
      occupation: localCust.occupation,
      category: localCust.category,
      dependentsCount: localCust.dependentsCount,
      dob: localCust.dob,
      age: localCust.age,
      gender: localCust.gender,
      phone: localCust.phone,
      email: localCust.email,
      labels: localCust.labels,
      createdAt: localCust.createdAt,
      illustrations: [],
      notes: [],
      dependents: [],
    };
  }

  return undefined;
}

export function getCustomerByIdOrName(idOrName?: string): CustomerFull {
  if (!idOrName) {
    return {
      id: 'KH001',
      name: 'Khách hàng mặc định',
      occupation: 'Nhân viên văn phòng',
      category: 'main',
      dependentsCount: 0,
      dob: '24/01/1992',
      age: 34,
      gender: 'Nam',
      phone: '84333233192',
      email: 'khachhang@gmail.com',
      labels: ['vip'],
      createdAt: '2026-06-20',
      illustrations: [],
      notes: [],
      dependents: [],
    };
  }

  const found = findCustomerById(idOrName);
  if (found) return found;

  return {
    id: idOrName,
    name: idOrName,
    occupation: 'Khách hàng',
    category: 'main',
    dependentsCount: 0,
    dob: '01/01/1992',
    age: 34,
    gender: 'Nam',
    phone: '0900000000',
    email: 'khachhang@example.com',
    labels: ['new'],
    createdAt: '2026-06-20',
    illustrations: [],
    notes: [],
    dependents: [],
  };
}